import { useEffect, useRef, useState } from "react";
import {
  Code2,
  FileText,
  Languages,
  ListChecks,
  Mail,
  type Sparkles,
  WrapText,
} from "lucide-react";
import { Button, Modal, Spinner } from "@/components/ui";
import { useAppStore, useModelsStore, useSettingsStore } from "@/stores";
import { QUICK_ACTION_LABEL, runQuickAction, type QuickActionKind } from "@/services/ai/chat/quickActionService";
import { logger } from "@/services/logger";
import { cn } from "@/utils";

const log = logger.scope("quick-actions");

const QUICK_ACTION_ICON: Record<QuickActionKind, typeof Sparkles> = {
  summarize: FileText,
  rewrite: WrapText,
  translate: Languages,
  "explain-code": Code2,
  "generate-email": Mail,
  "convert-markdown": FileText,
  "extract-actions": ListChecks,
};

const ACTIONS: QuickActionKind[] = [
  "summarize",
  "rewrite",
  "translate",
  "explain-code",
  "generate-email",
  "convert-markdown",
  "extract-actions",
];

/**
 * QuickActionsToolbar
 *
 * A floating menu that appears above any selected text in the app (mounted
 * once in `AppLayout`) offering the spec'd one-shot AI transforms. Only
 * detects selections made via `window.getSelection()` — rendered text
 * (messages, notes, etc.), not raw `<textarea>`/`<input>` selections, which
 * don't expose selection via that API.
 */
export function QuickActionsToolbar() {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedText, setSelectedText] = useState("");
  const [running, setRunning] = useState<QuickActionKind | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onSelectionChange() {
      const selection = window.getSelection();
      const text = selection?.toString().trim() ?? "";
      if (!text || !selection || selection.rangeCount === 0) {
        setPosition(null);
        return;
      }
      // Ignore selections inside the toolbar itself (e.g. copying a label).
      if (toolbarRef.current?.contains(selection.anchorNode)) return;

      const rect = selection.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setPosition(null);
        return;
      }
      setSelectedText(text);
      setPosition({ x: rect.left + rect.width / 2, y: rect.top });
    }

    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  async function run(kind: QuickActionKind) {
    const model = useModelsStore.getState().activeModelId ?? useSettingsStore.getState().defaultModelId;
    if (!model) {
      useAppStore.getState().addToast({
        title: "No model selected",
        description: "Pick a model on the Models page before running a Quick Action.",
        variant: "warning",
      });
      return;
    }
    const baseUrl = useSettingsStore.getState().ollamaHost;
    setPosition(null);
    setRunning(kind);
    setResult("");
    try {
      const full = await runQuickAction(kind, selectedText, { baseUrl, model, onDelta: setResult });
      setResult(full);
    } catch (err) {
      log.error("Quick Action failed", err);
      useAppStore.getState().addToast({ title: "Quick Action failed", variant: "error" });
      setResult(null);
      setRunning(null);
    }
  }

  async function copyResult() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
    } catch {
      // Clipboard permission denied — no-op.
    }
  }

  function insertIntoComposer() {
    if (!result) return;
    useAppStore.getState().setPendingComposerText(result);
    setRunning(null);
    setResult(null);
  }

  return (
    <>
      {position && (
        <div
          ref={toolbarRef}
          className="animate-scale-in fixed z-toast flex -translate-x-1/2 -translate-y-full gap-0.5 rounded-md border border-border-strong bg-bg-overlay p-1 shadow-modal"
          style={{ left: position.x, top: position.y - 8 }}
        >
          {ACTIONS.map((kind) => {
            const Icon = QUICK_ACTION_ICON[kind];
            return (
              <button
                key={kind}
                type="button"
                aria-label={QUICK_ACTION_LABEL[kind]}
                title={QUICK_ACTION_LABEL[kind]}
                onClick={() => void run(kind)}
                className="flex h-7 w-7 items-center justify-center rounded-sm text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary"
              >
                <Icon size={14} strokeWidth={1.75} />
              </button>
            );
          })}
        </div>
      )}

      <Modal
        open={running !== null}
        onClose={() => {
          setRunning(null);
          setResult(null);
        }}
        title={running ? QUICK_ACTION_LABEL[running] : undefined}
        size="lg"
        footer={
          result !== null && (
            <>
              <Button variant="ghost" size="sm" onClick={() => void copyResult()}>
                Copy
              </Button>
              <Button variant="primary" size="sm" onClick={insertIntoComposer}>
                Insert into chat
              </Button>
            </>
          )
        }
      >
        {result === null || result === "" ? (
          <Spinner size={14} label="Thinking" />
        ) : (
          <p className={cn("selectable whitespace-pre-wrap text-base text-text-primary")}>{result}</p>
        )}
      </Modal>
    </>
  );
}
